/**
 * ITHR TalentLens — brand mark.
 * Concept: a 6-blade camera aperture (the "lens") with a confident
 * letter "T" inside, plus a small amber glint suggesting the focused
 * highlight of a candidate being seen clearly.
 *
 * Props:
 *   size — px (default 36)
 *   tone — "default" (gradient teal) | "white" (mono white) | "dark"
 */
export default function BrandLogo({ size = 36, tone = "default", className = "" }) {
  const stroke = tone === "white" ? "#FFFFFF" : tone === "dark" ? "#0E3A40" : "url(#tl-grad)";
  const fill = tone === "white" ? "#FFFFFF" : tone === "dark" ? "#0E3A40" : "url(#tl-grad)";
  const glint = tone === "white" ? "rgba(255,255,255,0.85)" : "#F59E0B";

  return (
    <svg
      viewBox="0 0 64 64"
      width={size}
      height={size}
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      aria-label="ITHR TalentLens"
      role="img"
    >
      <defs>
        <linearGradient id="tl-grad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#0E7490" />
          <stop offset="55%" stopColor="#155E75" />
          <stop offset="100%" stopColor="#083F4D" />
        </linearGradient>
        <radialGradient id="tl-inner" cx="40%" cy="40%" r="70%">
          <stop offset="0%" stopColor="#FFFFFF" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#FFFFFF" stopOpacity="0" />
        </radialGradient>
      </defs>

      {/* Outer aperture ring */}
      <circle cx="32" cy="32" r="28" fill={fill} />
      {/* Subtle inner highlight */}
      <circle cx="32" cy="32" r="28" fill="url(#tl-inner)" />

      {/* Aperture blades — 6 slanted slits */}
      <g
        fill="none"
        stroke={tone === "white" ? "rgba(14, 58, 64, 0.35)" : "rgba(255,255,255,0.16)"}
        strokeWidth="1.2"
        strokeLinecap="round"
      >
        {[0, 60, 120, 180, 240, 300].map((deg) => (
          <line
            key={deg}
            x1="32"
            y1="32"
            x2={32 + 22 * Math.cos((deg * Math.PI) / 180)}
            y2={32 + 22 * Math.sin((deg * Math.PI) / 180)}
            transform={`rotate(${deg / 2} 32 32)`}
          />
        ))}
      </g>

      {/* Inner ring (the iris) */}
      <circle
        cx="32"
        cy="32"
        r="17"
        fill="none"
        stroke={tone === "white" ? "rgba(14,58,64,0.5)" : "rgba(255,255,255,0.22)"}
        strokeWidth="1.4"
      />

      {/* Letter T — confident, sans, slightly chunky */}
      <g
        fill={tone === "white" ? "#0E3A40" : "#FFFFFF"}
        style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800 }}
      >
        {/* T horizontal arm */}
        <rect x="22" y="22" width="20" height="4.2" rx="1" />
        {/* T vertical stem */}
        <rect x="29.9" y="22" width="4.2" height="20" rx="1" />
      </g>

      {/* Amber glint — the "focused highlight" */}
      <circle cx="48" cy="18" r="3.2" fill={glint} />
      <circle cx="48" cy="18" r="1.4" fill="#FFFFFF" opacity="0.85" />
    </svg>
  );
}

/** Inline wordmark — logo + name */
export function BrandWordmark({ size = 36 }) {
  return (
    <div className="flex items-center gap-2.5">
      <BrandLogo size={size} />
      <div className="leading-none">
        <div className="font-heading font-bold text-[15px] tracking-tight text-[var(--text-primary)]">
          ITHR <span className="gradient-text">TalentLens</span>
        </div>
        <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-[var(--text-muted)] mt-1">
          See talent clearly · v1.4
        </div>
      </div>
    </div>
  );
}
